import React, { useState, useEffect } from 'react'
import { Loader2, AlertTriangle, AlertCircle } from 'lucide-react'
import { Drawer } from '../ui'
import AccountPicker from '../ui/AccountPicker'
import CategoryPicker from '../ui/CategoryPicker'
import TagPicker from '../attribution-tags/TagPicker'
import { useToast } from '../../contexts/ToastContext'
import useAssetStore from '../../stores/assetStore'
import { Asset, AssetStatus } from '../../types/asset'
import { assetService } from '../../services/assetService'

interface EditAssetDrawerProps {
  isOpen: boolean
  onClose: () => void
  asset: Asset | null
  onSave?: () => void
}

const EditAssetDrawer: React.FC<EditAssetDrawerProps> = ({ isOpen, onClose, asset, onSave }) => {
  const { showSuccess, showError } = useToast()
  const { updateAsset } = useAssetStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const [status, setStatus] = useState<AssetStatus>('Active')
  const [accountIds, setAccountIds] = useState<string[]>([])
  const [hashtagIds, setHashtagIds] = useState<number[]>([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const [activeValidationWarning, setActiveValidationWarning] = useState<string | null>(null)
  const [activeActivationName, setActiveActivationName] = useState<string | null>(null)

  // Populate form when asset changes
  useEffect(() => {
    if (asset) {
      setName(asset.name)
      setDescription(asset.description || '')
      setStatus(asset.status)
      setActiveActivationName(null)

      // Load full asset details to get hashtags, accounts, and categories
      const loadAssetDetails = async () => {
        setIsLoadingDetails(true)
        try {
          // Load full asset details (includes asset_hashtags and asset_category_mappings)
          const fullAsset = await assetService.getAsset(asset.id, asset.customer_id)

          // Extract category IDs from asset_category_mappings
          if (fullAsset.asset_category_mappings && Array.isArray(fullAsset.asset_category_mappings)) {
            const catIds = fullAsset.asset_category_mappings.map((acm) => acm.category_id)
            setCategoryIds(catIds)
          } else {
            setCategoryIds([])
          }

          // Extract hashtag IDs from asset_hashtags, only include Active tags
          if (fullAsset.asset_hashtags && Array.isArray(fullAsset.asset_hashtags)) {
            const activeHashtagIds = fullAsset.asset_hashtags
              .filter((ah) => ah.social_hashtags?.status?.toLowerCase() === 'active')
              .map((ah) => Number(ah.hashtag_id))
            setHashtagIds(activeHashtagIds)
          } else {
            setHashtagIds([])
          }

          // Load accounts for the asset
          const accounts = await assetService.getAssetAccounts(asset.id, asset.customer_id)
          setAccountIds(accounts.map((acc) => acc.id))

          // Check if asset is part of any active activation
          const activations = await assetService.getAssetActivations(asset.id, asset.customer_id)
          const activeActivation = activations.find((a) => a.status === 'Active')
          if (activeActivation) {
            setActiveActivationName(activeActivation.name)
          }
        } catch (error) {
          console.error('Failed to load asset details:', error)
          setCategoryIds([])
          setAccountIds([])
          setHashtagIds([])
        } finally {
          setIsLoadingDetails(false)
        }
      }
      loadAssetDetails()
    }
  }, [asset])

  const resetForm = () => {
    setName('')
    setDescription('')
    setCategoryIds([])
    setStatus('Active')
    setAccountIds([])
    setHashtagIds([])
    setActiveValidationWarning(null)
    setActiveActivationName(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!asset) return

    // Validation
    if (!name.trim()) {
      showError('Validation Error', 'Asset name is required')
      return
    }

    if (name.length > 30) {
      showError('Validation Error', 'Asset name must be 30 characters or less')
      return
    }

    // Validate requirements for Active status (case-insensitive check)
    if (status.toLowerCase() === 'active') {
      const missingFields: string[] = []
      if (accountIds.length === 0) missingFields.push('at least one account')
      if (hashtagIds.length === 0) missingFields.push('at least one attribution tag')
      if (categoryIds.length === 0) missingFields.push('at least one post format')

      if (missingFields.length > 0) {
        const message = missingFields.length === 1
          ? missingFields[0]
          : missingFields.length === 2
            ? `${missingFields[0]} and ${missingFields[1]}`
            : `${missingFields.slice(0, -1).join(', ')} and ${missingFields[missingFields.length - 1]}`
        setActiveValidationWarning(
          `To set this asset to Active, you must select ${message}.`
        )
        return
      }
    }

    // Clear any previous warning
    setActiveValidationWarning(null)

    setIsUpdating(true)

    try {
      const updated = await updateAsset(asset.id, asset.customer_id, {
        name: name.trim(),
        description: description.trim() || undefined,
        status
      })

      // Update category associations
      await assetService.setAssetCategories(asset.id, asset.customer_id, categoryIds)

      // Update account associations
      await assetService.setAssetAccounts(asset.id, asset.customer_id, accountIds)

      // Update hashtag associations
      await assetService.setAssetHashtags(asset.id, asset.customer_id, hashtagIds)

      showSuccess('Asset Updated', `"${updated.name}" has been updated successfully`)

      // Notify parent to reload with current filters
      onSave?.()

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to Update Asset',
        errorMessage.includes('already exists')
          ? 'An asset with this name already exists'
          : 'Please try again later'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title='Edit Asset'>
      <form onSubmit={handleSubmit} className='flex flex-col h-full'>
        <div className='flex-1 overflow-y-auto px-6 py-4 space-y-3'>
          {/* Active Activation Info Banner */}
          {activeActivationName && (
            <div className='flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
              <AlertCircle className='h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0' />
              <p className='text-xs text-blue-700'>
                This asset is part of an active activation ("{activeActivationName}"). Only the name and description can be modified.
              </p>
            </div>
          )}

          {/* Active Status Validation Warning */}
          {activeValidationWarning && (
            <div className='flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
              <AlertTriangle className='h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0' />
              <p className='text-xs text-amber-800'>{activeValidationWarning}</p>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor='name' className='block text-xs font-medium text-gray-700 mb-1'>
              Asset Name <span className='text-red-500'>*</span>
            </label>
            <input
              id='name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isUpdating}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'
              placeholder='e.g., Game Day Recap, Athlete Spotlight'
              maxLength={30}
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor='description' className='block text-xs font-medium text-gray-700 mb-1'>
              Description
            </label>
            <textarea
              id='description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isUpdating}
              rows={3}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none'
              placeholder='Brief description of this asset...'
            />
          </div>

          {/* Post Format */}
          <CategoryPicker
            selectedCategoryIds={categoryIds}
            onChange={setCategoryIds}
            customerId={asset?.customer_id}
            disabled={isUpdating || isLoadingDetails || !!activeActivationName}
          />

          {/* Platform Accounts */}
          <AccountPicker
            selectedAccountIds={accountIds}
            onChange={setAccountIds}
            customerId={asset?.customer_id}
            disabled={isUpdating || isLoadingDetails || !!activeActivationName}
          />

          {/* Attribution Tags */}
          <TagPicker
            selectedTagIds={hashtagIds}
            onChange={setHashtagIds}
            customerId={asset?.customer_id}
            disabled={isUpdating || isLoadingDetails || !!activeActivationName}
          />

          {/* Status */}
          <div>
            <label htmlFor='status' className='block text-xs font-medium text-gray-700 mb-1'>
              Status
            </label>
            <select
              id='status'
              value={status}
              onChange={(e) => setStatus(e.target.value as AssetStatus)}
              disabled={isUpdating || !!activeActivationName}
              className='w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed'
            >
              <option value='Draft'>Draft</option>
              <option value='Active'>Active</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className='border-t border-gray-200 px-6 py-4 bg-gray-50'>
          <div className='flex space-x-3'>
            <button
              type='button'
              onClick={handleClose}
              disabled={isUpdating}
              className='flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isUpdating}
              className='flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isUpdating ? (
                <>
                  <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </form>
    </Drawer>
  )
}

export default EditAssetDrawer
